import { Redirect, Stack, useSegments } from 'expo-router';
import { useEffect, useState } from "react";
import { getStreamClient } from "@/services/stream/client";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { getPassportToken, getStreamAuth, getUserProfile, logout } from "@/services/auth/tokenStore";
import { NotificationProvider } from "@/services/notifications/provider.web";
import { authEventEmitter } from "@/services/auth/authEvents";
import { Chat } from "stream-chat-react";
import 'stream-chat-react/dist/css/v2/index.css';
import Head from 'expo-router/head';
import { useServiceWorker } from "@/hooks/useServiceWorker";
import { UpdateNotification } from "@/components/UpdateNotification";
import { initSentry, Sentry } from "@/services/sentry";

initSentry();

// Using stream-chat-react for web compatibility

export const unstable_settings = {
    anchor: '(tabs)',
};

function RootLayout() {
    const segments = useSegments();
    const [ready, setReady] = useState(false);
    const [authenticated, setAuthenticated] = useState(false);
    const { newVersionAvailable, updateServiceWorker } = useServiceWorker();

    useEffect(() => {
        let cancelled = false;

        async function init() {
            try {
                const passportToken = await getPassportToken();
                const streamAuth = await getStreamAuth();

                if (passportToken && streamAuth) {
                    const client = getStreamClient();
                    if (!client.userID) {
                        const { name, image } = await getUserProfile();
                        await client.connectUser(
                            {
                                id: streamAuth.userId,
                                name: name ?? undefined,
                                image: image ?? undefined,
                            },
                            streamAuth.token
                        );
                    }
                    if (!cancelled) setAuthenticated(true);
                } else {
                    if (!cancelled) setAuthenticated(false);
                }

                if (!cancelled) setReady(true);
            } catch (err) {
                console.log("Initialization error:", err);
                if (!cancelled) setReady(true);
            }
        }

        init();
        
        const unsubscribe = authEventEmitter.subscribe(() => {
            logout();
        });

        return () => { 
            cancelled = true; 
            unsubscribe();
        };
    }, []);

    if (!ready) return null;

    const inAuthGroup = segments[0] === '(auth)';
    if (authenticated && inAuthGroup) return <Redirect href="/(tabs)" />;
    if (!authenticated && !inAuthGroup) return <Redirect href="/(auth)/login" />;

    return (
        <>
            <Head>
                <link rel="manifest" href="/manifest.json" />
                <meta name="theme-color" content="#14a5c6" />
                <meta name="apple-mobile-web-app-capable" content="yes" />
                <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
            </Head>
            <NotificationProvider>
                <SafeAreaProvider>
                    <Chat client={getStreamClient()}>
                        <Stack screenOptions={{ headerShown: false }}>
                            <Stack.Screen name="(tabs)" />
                        </Stack>
                    </Chat>
                    <UpdateNotification
                        visible={newVersionAvailable}
                        onUpdate={updateServiceWorker}
                    />
                </SafeAreaProvider>
            </NotificationProvider>
        </>
    );
}

export default Sentry.wrap(RootLayout);